import { mdiChevronRight } from '@mdi/js';
import _ from 'lodash';
import React from 'react';
import {
    ProviderGuarantor,
    ProviderPayer,
    ProviderPayerSubscriber,
    ProviderSubscriber,
    ProviderSubscriberPatient,
    SupportGuarantor,
    SupportPayer,
    SupportPayerSubscriber,
    SupportSubscriber,
    SupportSubscriberPatient,
} from '../../../api';
import { LoadingMeter } from '../../base';
import { CardAction } from '../cards/cards';
import { ClaimOverviewSectionMenu } from './claim-overview-section-menu';
import * as S from './claim-overview.styles';
import * as SectionS from '../claim-section.styles';
import {
    formatAddress,
    formatGuarantorName,
    formatPatientDemographics,
    formatPayerDescription,
    formatPayerTitle,
    formatPersonName,
    formatPhone,
    formatRelationshipLabel,
    formatSubscriberSummary,
} from './claim-overview.utils';

const PAYER_ROLES = ['primary', 'secondary', 'tertiary'] as const;
type PayerRole = (typeof PAYER_ROLES)[number];

export type ClaimOverviewSection = 'patient' | PayerRole | 'guarantor';
export type ClaimOverviewSectionActions = Partial<Record<ClaimOverviewSection, CardAction[]>>;

export interface IClaimOverviewProps {
    loading: boolean;
    guarantor?: ProviderGuarantor | SupportGuarantor;
    subscriberPatient?: ProviderSubscriberPatient | SupportSubscriberPatient;
    payerSubscribers?: ProviderPayerSubscriber[] | SupportPayerSubscriber[];
    payers?: ProviderPayer[] | SupportPayer[];
    subscribers?: ProviderSubscriber[] | SupportSubscriber[];
    thirdPartyAdministrators?: Array<ProviderPayer | SupportPayer | undefined>;
    patientDetailsHref?: string;
    viewFullDetailsHref?: string;
    onViewFullDetails?: () => void;
    embedded?: boolean;
    sectionActions?: ClaimOverviewSectionActions;
}

const getPayerAtIndex = (
    index: number,
    payerSubscribers?: ProviderPayerSubscriber[] | SupportPayerSubscriber[],
    payers?: ProviderPayer[] | SupportPayer[],
): ProviderPayer | SupportPayer | undefined => {
    const payerSubscriber = _.get(payerSubscribers, index);
    if (!payerSubscriber) {
        return;
    }

    return _.find(payers, { id: _.get(payerSubscriber, 'payer.id') }) || payerSubscriber.payer;
};

export const ClaimOverview: React.FC<IClaimOverviewProps> = ({
    loading,
    guarantor,
    subscriberPatient,
    payerSubscribers,
    payers,
    subscribers,
    thirdPartyAdministrators,
    patientDetailsHref,
    viewFullDetailsHref,
    onViewFullDetails,
    embedded = false,
    sectionActions,
}) => {
    const patient = subscriberPatient?.patient;

    const renderViewFullDetails = (): React.ReactNode => {
        const label = (
            <>
                View full details <S.ChevronIcon path={mdiChevronRight} />
            </>
        );

        if (viewFullDetailsHref) {
            return <S.ViewDetailsLink to={viewFullDetailsHref}>{() => label}</S.ViewDetailsLink>;
        }

        if (onViewFullDetails) {
            return (
                <S.ViewDetailsButton type="button" variant="unstyled" onClick={onViewFullDetails}>
                    {label}
                </S.ViewDetailsButton>
            );
        }

        return null;
    };

    const renderSectionMenu = (section: ClaimOverviewSection): React.ReactNode => {
        const actions = sectionActions?.[section];
        if (!actions?.length) {
            return null;
        }

        return <ClaimOverviewSectionMenu actions={actions} />;
    };

    const renderField = (label: string, value?: React.ReactNode, emphasized?: boolean): React.ReactElement => (
        <S.Field>
            <S.FieldLabel>{label}</S.FieldLabel>
            {value ? <S.FieldValue $emphasized={emphasized}>{value}</S.FieldValue> : <S.EmptyValue>Not on file</S.EmptyValue>}
        </S.Field>
    );

    const renderPatientSection = (): React.ReactElement => {
        const patientName = patient ? formatPersonName(patient.first, patient.last, patient.suffix) : undefined;
        const nameContent = patientName || 'Not on file';

        return (
            <S.Section $first>
                <S.SectionHeader>
                    <S.SectionLabel>Patient</S.SectionLabel>
                    {renderSectionMenu('patient')}
                </S.SectionHeader>
                <S.NameRow>
                    {patientDetailsHref && patientName ? (
                        <S.NameLink to={patientDetailsHref}>{() => nameContent}</S.NameLink>
                    ) : (
                        <S.Name>{nameContent}</S.Name>
                    )}
                </S.NameRow>
                {patient && <S.Demographics>{formatPatientDemographics(patient)}</S.Demographics>}
                {renderField('Patient ID', patient?.externalId || patient?.id)}
                {renderField('Address', formatAddress(patient?.addresses))}
            </S.Section>
        );
    };

    const renderPayerSection = (role: PayerRole, index: number): React.ReactElement => {
        const payerSubscriber = _.get(payerSubscribers, index);
        const payer = getPayerAtIndex(index, payerSubscribers, payers);
        const subscriber = _.find(subscribers, { id: _.get(payerSubscriber, 'subscriber.id') });
        const thirdPartyAdministrator = thirdPartyAdministrators?.[index];
        const hasPayer = Boolean(payerSubscriber && payer);
        const payerDescription = formatPayerDescription(payer);

        return (
            <S.Section key={role}>
                <S.SectionHeader>
                    <S.RoleBadge $variant={role}>{role}</S.RoleBadge>
                    {renderSectionMenu(role)}
                </S.SectionHeader>
                {hasPayer ? (
                    <>
                        <S.Name>{formatPayerTitle(payer)}</S.Name>
                        {payerDescription && <S.Subtitle>{payerDescription}</S.Subtitle>}
                        {renderField('TPA', thirdPartyAdministrator?.name || payer?.name)}
                        {renderField('Subscriber', formatSubscriberSummary(subscriber))}
                        {renderField('Member ID', payerSubscriber?.memberId, true)}
                    </>
                ) : (
                    <S.EmptyValue>Not on file</S.EmptyValue>
                )}
            </S.Section>
        );
    };

    const renderGuarantorSection = (): React.ReactElement => {
        const guarantorName = formatGuarantorName(guarantor);
        const relationship = formatRelationshipLabel(subscriberPatient);
        const phone = formatPhone(guarantor?.phone);

        return (
            <S.Section>
                <S.SectionHeader>
                    <S.RoleBadge $variant="guarantor">Guarantor</S.RoleBadge>
                    {renderSectionMenu('guarantor')}
                </S.SectionHeader>
                {guarantor ? (
                    <>
                        <S.Name>{guarantorName}</S.Name>
                        {relationship && <S.Subtitle>{relationship}</S.Subtitle>}
                        {renderField('Address', formatAddress(guarantor.addresses))}
                        <S.Field>
                            <S.FieldLabel>Phone</S.FieldLabel>
                            {phone ? (
                                <S.FieldValue>{phone}</S.FieldValue>
                            ) : (
                                <S.EmptyValue>No phone on file</S.EmptyValue>
                            )}
                        </S.Field>
                    </>
                ) : (
                    <S.EmptyValue>Not on file</S.EmptyValue>
                )}
            </S.Section>
        );
    };

    const body = loading ? (
        <S.Loader>
            <LoadingMeter />
        </S.Loader>
    ) : (
        <S.Content>
            {renderPatientSection()}
            {PAYER_ROLES.map((role, index) => renderPayerSection(role, index))}
            {renderGuarantorSection()}
        </S.Content>
    );

    if (embedded) {
        return (
            <SectionS.ClaimSectionBlock>
                <SectionS.ClaimSectionHeader>
                    <SectionS.ClaimSectionTitle>Claim Overview</SectionS.ClaimSectionTitle>
                    {renderViewFullDetails()}
                </SectionS.ClaimSectionHeader>
                {body}
            </SectionS.ClaimSectionBlock>
        );
    }

    return (
        <S.StyledTitleCard size="large" title="Claim Overview" caption={renderViewFullDetails()}>
            {body}
        </S.StyledTitleCard>
    );
};
